(function () {
  'use strict';

  // Sheet rules (same as the DTF print shop this flow is modelled on)
  var SHEET_W = 22.5;          // printable width, inches
  var GAP = 0.25;              // space between designs, inches
  var MAX_LEN = 390;           // longest sheet we print, inches
  var MIN_LEN = 1;             // shortest ready sheet, inches
  var BEST_DPI = 300;          // a design's default size is its pixels at 300 DPI
  var MIN_DPI = 250;           // ...and it can grow until it hits 250 DPI
  var MIN_SHEET_PX = Math.round(SHEET_W * MIN_DPI); // 5625 px across for a ready sheet
  var WIDTH_STEP = 0.5;
  var MIN_WIDTH = 0.5;
  var MAX_PIECES = 999;
  var ANALYSE_PX = 4000;       // longest side of the working copy used for trimming (keeps sizes pixel-accurate)
  var PREVIEW_PX = 1000;       // longest side of the copy drawn on the sheet preview
  var ALPHA_MIN = 16;          // alpha below this counts as transparent
  var MAX_CANVAS_AREA = 12e6;  // keep preview canvases under mobile Safari's limit

  function formatMoney(paise) {
    var n = (Number(paise) || 0) / 100;
    return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function formatRate(paise) {
    var n = (Number(paise) || 0) / 100;
    return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  }

  function fmtIn(n) {
    return (Math.round(n * 10) / 10).toFixed(1);
  }

  // Never overstate how big a file can go
  function fmtInFloor(n) {
    return (Math.floor(n * 10 + 1e-6) / 10).toFixed(1);
  }

  function fmtMetres(inches) {
    return (inches * 0.0254).toFixed(4).replace(/\.?0+$/, '') || '0';
  }

  // Price for a length the way the sheet shows it: metres rounded to 4 places, times the rate
  function lengthPrice(inches, perMetre) {
    return Math.round(Math.round(inches * 0.0254 * 1e4) / 1e4 * perMetre);
  }

  function plural(n, one, many) {
    return n + ' ' + (n === 1 ? one : many);
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fileExt(file) {
    var m = /\.([a-z0-9]+)$/i.exec(file.name || '');
    return m ? m[1].toLowerCase() : '';
  }

  function isPdf(file) {
    return file.type === 'application/pdf' || fileExt(file) === 'pdf';
  }

  function isPng(file) {
    return file.type === 'image/png' || fileExt(file) === 'png';
  }

  var scriptPromises = {};
  function loadScript(src) {
    if (!scriptPromises[src]) {
      scriptPromises[src] = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = function () { reject(new Error('Could not load ' + src)); };
        document.head.appendChild(s);
      });
    }
    return scriptPromises[src];
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('decode')); };
      img.src = url;
    });
  }

  function canvasToBlob(canvas, type) {
    return new Promise(function (resolve) {
      canvas.toBlob(function (blob) { resolve(blob); }, type || 'image/png');
    });
  }

  // Draws the file into a working canvas (longest side <= ANALYSE_PX) and reports its real size.
  // PNG: real size in pixels. PDF: real size in inches (page size), always treated as sharp.
  async function rasterise(file, data) {
    if (isPdf(file)) {
      await loadScript(data.pdfjs);
      var lib = window.pdfjsLib;
      lib.GlobalWorkerOptions.workerSrc = data.pdfjsWorker;
      var doc = await lib.getDocument({ data: await file.arrayBuffer() }).promise;
      var page = await doc.getPage(1);
      var base = page.getViewport({ scale: 1 });
      var scale = ANALYSE_PX / Math.max(base.width, base.height);
      var vp = page.getViewport({ scale: scale });
      var pdfCanvas = document.createElement('canvas');
      pdfCanvas.width = Math.max(1, Math.round(vp.width));
      pdfCanvas.height = Math.max(1, Math.round(vp.height));
      await page.render({ canvasContext: pdfCanvas.getContext('2d'), viewport: vp }).promise;
      return { canvas: pdfCanvas, vector: true, widthIn: base.width / 72, heightIn: base.height / 72 };
    }

    var url = URL.createObjectURL(file);
    try {
      var img = await loadImage(url);
      var w = img.naturalWidth;
      var h = img.naturalHeight;
      var s = Math.min(1, ANALYSE_PX / Math.max(w, h));
      var canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(w * s));
      canvas.height = Math.max(1, Math.round(h * s));
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      return { canvas: canvas, vector: false, pxW: w, pxH: h };
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  // Bounding box of the visible artwork + whether the file is just one flat colour.
  function inspect(canvas) {
    var w = canvas.width;
    var h = canvas.height;
    var px = canvas.getContext('2d').getImageData(0, 0, w, h).data;
    var x0 = w, y0 = h, x1 = -1, y1 = -1, opaque = 0;
    var minR = 255, minG = 255, minB = 255, maxR = 0, maxG = 0, maxB = 0;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var i = (y * w + x) * 4;
        if (px[i + 3] < ALPHA_MIN) continue;
        opaque++;
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
        var r = px[i], g = px[i + 1], b = px[i + 2];
        if (r < minR) minR = r; if (r > maxR) maxR = r;
        if (g < minG) minG = g; if (g > maxG) maxG = g;
        if (b < minB) minB = b; if (b > maxB) maxB = b;
      }
    }
    var flat = opaque >= w * h * 0.98 && maxR - minR <= 12 && maxG - minG <= 12 && maxB - minB <= 12;
    return { empty: opaque === 0, flat: flat, x0: x0, y0: y0, x1: x1, y1: y1 };
  }

  function cropCanvas(src, x, y, w, h) {
    var c = document.createElement('canvas');
    c.width = Math.max(1, w);
    c.height = Math.max(1, h);
    c.getContext('2d').drawImage(src, x, y, w, h, 0, 0, c.width, c.height);
    return c;
  }

  function downscale(src, maxSide) {
    var s = Math.min(1, maxSide / Math.max(src.width, src.height));
    if (s === 1) return src;
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(src.width * s));
    c.height = Math.max(1, Math.round(src.height * s));
    c.getContext('2d').drawImage(src, 0, 0, c.width, c.height);
    return c;
  }

  function thumbUrl(src) {
    return downscale(src, 112).toDataURL('image/png');
  }

  // First-fit decreasing-height shelf packing, left to right, 0.25 in apart, no edge margin.
  function pack(designs) {
    var rects = [];
    designs.forEach(function (item, order) {
      var h = item.widthIn * item.aspect;
      for (var p = 0; p < item.pieces; p++) rects.push({ item: item, w: item.widthIn, h: h, order: order });
    });
    rects.sort(function (a, b) { return (b.h - a.h) || (a.order - b.order); });

    var shelves = [];
    rects.forEach(function (r) {
      var shelf = null;
      for (var i = 0; i < shelves.length; i++) {
        var s = shelves[i];
        if (r.h <= s.h + 1e-9 && s.x + r.w <= SHEET_W + 1e-9) { shelf = s; break; }
      }
      if (!shelf) {
        var last = shelves[shelves.length - 1];
        shelf = { y: last ? last.y + last.h + GAP : 0, h: r.h, x: 0 };
        shelves.push(shelf);
      }
      r.x = shelf.x;
      r.y = shelf.y;
      shelf.x += r.w + GAP;
    });

    var lastShelf = shelves[shelves.length - 1];
    return { rects: rects, length: lastShelf ? lastShelf.y + lastShelf.h : 0 };
  }

  function drawSheet(canvas, layout, cssWidth, opaqueBg) {
    var ppi = cssWidth / SHEET_W;
    var cssHeight = Math.max(1, layout.length * ppi);
    var k = Math.min(window.devicePixelRatio || 1, Math.sqrt(MAX_CANVAS_AREA / (cssWidth * cssHeight)));
    canvas.width = Math.max(1, Math.round(cssWidth * k));
    canvas.height = Math.max(1, Math.round(cssHeight * k));
    canvas.style.width = cssWidth + 'px';
    canvas.style.height = cssHeight + 'px';
    var ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (opaqueBg) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    var f = ppi * k;
    layout.rects.forEach(function (r) {
      ctx.drawImage(r.item.preview, r.x * f, r.y * f, r.w * f, r.h * f);
    });
    return cssHeight;
  }

  function initSheetBuilder(root) {
    var sectionId = root.id.replace('ppc-', '');
    var dataEl = document.getElementById('dsb-data-' + sectionId);
    if (!dataEl) return;
    var data = JSON.parse(dataEl.textContent);
    var unit = data.unitVariant;
    var pricePerMetre = data.pricePerMetre; // paise
    var maxFileMb = data.maxFileMb || 20;
    var gallery = data.gallery || [];

    var q = function (sel) { return root.querySelector(sel); };
    var filesEl = q('[data-dsb-files]');
    var actionsEl = q('[data-dsb-actions]');
    var dropzone = q('[data-dsb-dropzone]');
    var emptyEl = q('[data-dsb-empty]');
    var sheetEl = q('[data-dsb-sheet]');
    var sheetDims = q('[data-dsb-sheet-dims]');
    var sheetPrice = q('[data-dsb-sheet-price]');
    var sheetWarn = q('[data-dsb-sheet-warn]');
    var rulerY = q('[data-dsb-ruler-y]');
    var canvasWrap = q('[data-dsb-canvas-wrap]');
    var canvas = q('[data-dsb-canvas]');
    var wholeBtn = q('[data-dsb-whole]');
    var readyPreviews = q('[data-dsb-ready-previews]');
    var summaryEmpty = q('[data-dsb-summary-empty]');
    var summaryCalc = q('[data-dsb-summary-calc]');
    var lenLabel = q('[data-dsb-len-label]');
    var lenVal = q('[data-dsb-len-val]');
    var piecesEl = q('[data-dsb-pieces]');
    var billedEl = q('[data-dsb-billed]');
    var grandEl = q('[data-dsb-grand]');
    var errorEl = q('[data-dsb-error]');
    var statusEl = q('[data-dsb-status]');
    var submitBtn = q('[data-dsb-submit]');
    var notes = q('[data-dsb-notes]');
    var labelSheet = q('[data-dsb-label-sheet]');
    var labelDesign = q('[data-dsb-label-design]');
    var noteWrap = q('[data-dsb-note-wrap]');

    var items = [];
    var nextId = 1;
    var layout = { rects: [], length: 0 };
    var totals = null;

    // ---------- Files ----------

    function addFiles(fileList, kind, preset) {
      Array.prototype.forEach.call(fileList, function (file) {
        var item = {
          id: nextId++, kind: kind, file: file, name: file.name, status: 'checking',
          error: '', notice: '', pieces: 1, widthIn: 0, maxW: 0, aspect: 1, heightIn: 0, preset: preset || null
        };
        items.push(item);
        item.el = buildCard(item);
        filesEl.appendChild(item.el);
        analyse(item);
      });
      errorEl.textContent = '';
      statusEl.textContent = '';
      refresh();
    }

    async function analyse(item) {
      var file = item.file;
      try {
        if (!isPng(file) && !isPdf(file)) throw new Error('Please upload a PNG or PDF file.');
        if (file.size > maxFileMb * 1024 * 1024) {
          throw new Error('This file is ' + (file.size / 1048576).toFixed(1) + ' MB. The limit is ' + maxFileMb + ' MB.');
        }

        var r;
        try {
          r = await rasterise(file, data);
        } catch (e) {
          throw new Error('We could not read this file. Export it again as PNG or PDF.');
        }
        var info = inspect(r.canvas);
        if (info.empty) throw new Error('This file is empty. There is no design on it.');
        if (info.flat) {
          throw new Error((item.kind === 'sheet' ? 'This sheet' : 'This file') + ' is one flat colour with no design on it. Check your artwork, then export the PNG again.');
        }

        if (item.kind === 'sheet') {
          var tooNarrow = r.vector ? Math.abs(r.widthIn - SHEET_W) > 0.3 : r.pxW < MIN_SHEET_PX;
          if (tooNarrow) {
            item.kind = 'design';
            item.notice = r.vector
              ? 'This PDF is not 22.5 in wide, so we added it as a design you can size.'
              : 'This file is under ' + MIN_SHEET_PX + ' px across, so we added it as a design you can size.';
          }
        }

        if (item.kind === 'sheet') {
          item.heightIn = r.vector ? r.heightIn * (SHEET_W / r.widthIn) : r.pxH * SHEET_W / r.pxW;
          if (item.heightIn > MAX_LEN) {
            throw new Error('This sheet is ' + fmtIn(item.heightIn) + ' in long. The most we print in one sheet is ' + MAX_LEN + ' in.');
          }
          if (item.heightIn < MIN_LEN) throw new Error('This sheet is shorter than 1 in. Add some height and export it again.');
          item.preview = downscale(r.canvas, PREVIEW_PX * 2);
          item.thumb = thumbUrl(item.preview);
          item.previewUrl = item.preview.toDataURL('image/png');
        } else {
          // Designs are trimmed to their visible artwork
          var bw = info.x1 - info.x0 + 1;
          var bh = info.y1 - info.y0 + 1;
          var trimW, nativeW, maxW;
          if (r.vector) {
            trimW = bw / r.canvas.width * r.widthIn;
            nativeW = trimW;
            maxW = SHEET_W;
          } else {
            var s = r.canvas.width / r.pxW;
            var trimPxW = Math.min(r.pxW, Math.round(bw / s));
            nativeW = trimPxW / BEST_DPI;
            maxW = Math.min(SHEET_W, trimPxW / MIN_DPI);
          }
          item.aspect = bh / bw;
          item.maxW = Math.min(maxW, MAX_LEN / item.aspect);
          item.vector = r.vector;
          item.widthIn = Math.min(Math.max(nativeW, Math.min(MIN_WIDTH, item.maxW)), item.maxW);
          item.preview = downscale(cropCanvas(r.canvas, info.x0, info.y0, bw, bh), PREVIEW_PX);
          item.thumb = thumbUrl(item.preview);
        }
        if (item.preset) {
          item.pieces = clampPieces(item.preset.pieces);
          if (item.kind === 'design' && item.preset.widthIn) item.widthIn = clampWidth(item, item.preset.widthIn);
          item.preset = null;
        }
        item.status = 'ready';
      } catch (err) {
        item.status = 'error';
        item.error = err.message || 'Something went wrong with this file.';
      }
      var fresh = buildCard(item);
      if (item.el && item.el.parentNode) item.el.parentNode.replaceChild(fresh, item.el);
      item.el = fresh;
      refresh();
    }

    function removeItem(item) {
      items = items.filter(function (it) { return it !== item; });
      if (item.el && item.el.parentNode) item.el.parentNode.removeChild(item.el);
      errorEl.textContent = '';
      refresh();
    }

    function stepper(label, value, attr) {
      return '<div><span class="dsb__ctl-label">' + label + '</span>' +
        '<div class="dsb__stepper">' +
        '<button type="button" data-dsb-dec="' + attr + '" aria-label="Decrease ' + label + '">&minus;</button>' +
        '<input type="number" inputmode="decimal" data-dsb-val="' + attr + '" value="' + value + '" aria-label="' + label + '">' +
        '<button type="button" data-dsb-inc="' + attr + '" aria-label="Increase ' + label + '">+</button>' +
        '</div></div>';
    }

    function buildCard(item) {
      var el = document.createElement('div');
      el.className = 'dsb__file' + (item.status === 'error' ? ' is-error' : '');
      var thumb = item.thumb
        ? '<button type="button" class="dsb__thumb-btn" data-dsb-preview aria-label="Preview ' + escapeHtml(item.name) + '">' +
          '<img class="dsb__file-thumb" src="' + item.thumb + '" alt="" width="56" height="56">' +
          '<span class="dsb__thumb-zoom"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg></span></button>'
        : '<span class="dsb__file-thumb"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg></span>';

      var body = '<div class="dsb__file-top"><span class="dsb__file-name" title="' + escapeHtml(item.name) + '">' + escapeHtml(item.name) + '</span>' +
        '<button type="button" class="dsb__file-remove" data-dsb-remove>' + (item.status === 'checking' ? 'Cancel' : '&#10005; Remove') + '</button></div>';

      if (item.status === 'checking') {
        body += '<div class="dsb__file-meta">' + (item.kind === 'sheet' ? 'Ready sheet' : 'Design') + '</div>' +
          '<div class="dsb__file-progress"></div><div class="dsb__file-hint">Checking your file&hellip;</div>';
      } else if (item.status === 'error') {
        body += '<div class="dsb__file-error">' + escapeHtml(item.error) + '</div>';
      } else if (item.kind === 'sheet') {
        body += '<div class="dsb__file-meta" data-dsb-meta></div>' +
          '<div class="dsb__file-controls">' + stepper('Pieces', item.pieces, 'pieces') +
          '<span class="dsb__file-price" data-dsb-price></span></div>';
      } else {
        body += '<div class="dsb__file-meta" data-dsb-meta></div>' +
          '<div class="dsb__file-hint" data-dsb-hint></div>' +
          (item.notice ? '<div class="dsb__file-hint">' + escapeHtml(item.notice) + '</div>' : '') +
          '<div class="dsb__file-controls">' + stepper('Width (in)', fmtIn(item.widthIn), 'width') +
          stepper('Pieces', item.pieces, 'pieces') + '</div>';
      }
      el.innerHTML = thumb + '<div>' + body + '</div>';

      el.querySelector('[data-dsb-remove]').addEventListener('click', function () { removeItem(item); });
      var previewBtn = el.querySelector('[data-dsb-preview]');
      if (previewBtn) previewBtn.addEventListener('click', function () { showFilePreview(item); });
      bindStepper(el, item, 'pieces');
      if (item.kind === 'design') bindStepper(el, item, 'width');
      if (item.status === 'ready') updateCard(item, el);
      return el;
    }

    function clampPieces(n) {
      return Math.min(MAX_PIECES, Math.max(1, Math.round(n) || 1));
    }

    function clampWidth(item, n) {
      return Math.min(item.maxW, Math.max(Math.min(MIN_WIDTH, item.maxW), n));
    }

    function bindStepper(el, item, attr) {
      var input = el.querySelector('[data-dsb-val="' + attr + '"]');
      if (!input) return;
      var dec = el.querySelector('[data-dsb-dec="' + attr + '"]');
      var inc = el.querySelector('[data-dsb-inc="' + attr + '"]');

      function set(v, rewrite) {
        if (attr === 'pieces') item.pieces = clampPieces(v);
        else {
          item.overMax = v > item.maxW + 1e-6;
          item.widthIn = clampWidth(item, v);
        }
        if (rewrite) input.value = attr === 'pieces' ? item.pieces : fmtIn(item.widthIn);
        updateCard(item, el);
        refresh();
      }

      dec.addEventListener('click', function () {
        set(attr === 'pieces' ? item.pieces - 1 : item.widthIn - WIDTH_STEP, true);
      });
      inc.addEventListener('click', function () {
        set(attr === 'pieces' ? item.pieces + 1 : item.widthIn + WIDTH_STEP, true);
      });
      input.addEventListener('input', function () {
        var v = parseFloat(input.value);
        if (!isFinite(v) || v <= 0) return;
        set(v, false);
      });
      input.addEventListener('change', function () {
        var v = parseFloat(input.value);
        set(isFinite(v) && v > 0 ? v : (attr === 'pieces' ? item.pieces : item.widthIn), true);
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
      });
    }

    function updateCard(item, el) {
      el = el || item.el;
      if (!el || item.status !== 'ready') return;
      var meta = el.querySelector('[data-dsb-meta]');
      var pDec = el.querySelector('[data-dsb-dec="pieces"]');
      var pInc = el.querySelector('[data-dsb-inc="pieces"]');
      if (pDec) pDec.disabled = item.pieces <= 1;
      if (pInc) pInc.disabled = item.pieces >= MAX_PIECES;

      if (item.kind === 'sheet') {
        meta.textContent = SHEET_W + ' × ' + fmtIn(item.heightIn) + ' in';
        el.querySelector('[data-dsb-price]').textContent =
          formatMoney(lengthPrice(item.heightIn * item.pieces, pricePerMetre));
        return;
      }

      var h = item.widthIn * item.aspect;
      meta.textContent = 'Design · ' + fmtIn(item.widthIn) + ' × ' + fmtIn(h) + ' in';
      var hint = el.querySelector('[data-dsb-hint]');
      var atMax = item.widthIn >= item.maxW - 1e-6;
      if (item.vector) {
        hint.textContent = atMax ? 'Up to ' + fmtInFloor(item.maxW) + ' in on the sheet.' : 'Vector PDF — sharp at any size.';
        hint.classList.remove('is-max');
      } else {
        if (!atMax) item.overMax = false;
        hint.textContent = !atMax
          ? 'Sharp up to ' + fmtInFloor(item.maxW) + ' in.'
          : item.overMax
            ? 'Up to ' + fmtInFloor(item.maxW) + ' in for this file. Bigger would not print sharp.'
            : 'Biggest sharp size for this file.';
        hint.classList.toggle('is-max', atMax && item.overMax && item.maxW < SHEET_W);
      }
      var wDec = el.querySelector('[data-dsb-dec="width"]');
      var wInc = el.querySelector('[data-dsb-inc="width"]');
      wDec.disabled = item.widthIn <= Math.min(MIN_WIDTH, item.maxW) + 1e-6;
      wInc.disabled = atMax;
    }

    // ---------- Sheet + totals ----------

    function readyOf(kind) {
      return items.filter(function (it) { return it.status === 'ready' && it.kind === kind; });
    }

    function computeTotals() {
      var designs = readyOf('design');
      var sheets = readyOf('sheet');
      layout = pack(designs);
      var sheetsLen = 0, pieces = 0;
      sheets.forEach(function (s) { sheetsLen += s.heightIn * s.pieces; pieces += s.pieces; });
      designs.forEach(function (d) { pieces += d.pieces; });
      var totalIn = layout.length + sheetsLen;
      var billedCm = totalIn > 0 ? Math.ceil(totalIn * 2.54 - 1e-6) : 0;
      var cmPrice = unit ? unit.price : pricePerMetre / 100;
      return {
        designs: designs, sheets: sheets, pieces: pieces, totalIn: totalIn,
        billedCm: billedCm, total: billedCm * cmPrice
      };
    }

    function renderSheet() {
      var hasDesigns = layout.rects.length > 0;
      sheetEl.hidden = !hasDesigns;
      if (hasDesigns) {
        sheetDims.textContent = SHEET_W + ' × ' + fmtIn(layout.length) + ' in · ' + fmtMetres(layout.length) + ' m';
        sheetPrice.textContent = formatMoney(lengthPrice(layout.length, pricePerMetre));
        rulerY.textContent = fmtIn(layout.length) + ' in';
        var cssW = canvasWrap.clientWidth || 400;
        var cssH = drawSheet(canvas, layout, cssW, false);
        var maxH = parseFloat(getComputedStyle(canvasWrap).maxHeight) || 420;
        var clipped = cssH > maxH + 1;
        canvasWrap.classList.toggle('is-clipped', clipped);
        wholeBtn.hidden = !clipped;
        var tooLong = layout.length > MAX_LEN;
        sheetWarn.hidden = !tooLong;
        sheetWarn.textContent = tooLong
          ? 'This sheet is ' + fmtIn(layout.length) + ' in long. The most we print in one sheet is ' + MAX_LEN + ' in — lower the pieces or sizes.'
          : '';
      }

      readyPreviews.innerHTML = '';
      totals.sheets.forEach(function (s, i) {
        var box = document.createElement('div');
        box.className = 'dsb__ready-item';
        box.innerHTML = '<div class="dsb__sheet-head"><div><strong class="dsb__sheet-title">Ready sheet ' + (i + 1) + '</strong>' +
          '<span class="dsb__sheet-dims">' + SHEET_W + ' × ' + fmtIn(s.heightIn) + ' in · ' + plural(s.pieces, 'piece', 'pieces') + '</span></div>' +
          '<span class="dsb__sheet-price">' + formatMoney(lengthPrice(s.heightIn * s.pieces, pricePerMetre)) + '</span></div>' +
          '<img src="' + s.previewUrl + '" alt="' + escapeHtml(s.name) + '">';
        readyPreviews.appendChild(box);
      });

      emptyEl.hidden = hasDesigns || totals.sheets.length > 0;
    }

    function blockingReason() {
      if (!totals || (totals.designs.length + totals.sheets.length) === 0) return 'Add a ready sheet or a design first.';
      if (items.some(function (it) { return it.status === 'checking'; })) return 'Please wait for your files to finish checking.';
      if (items.some(function (it) { return it.status === 'error'; })) return 'Remove the files with problems, or choose other files.';
      if (layout.length > MAX_LEN) return 'Your designs sheet is longer than ' + MAX_LEN + ' in. Lower the pieces or sizes.';
      if (!unit) return 'Online ordering for DTF sheets is not set up yet. Please order on WhatsApp.';
      if (!unit.available) return 'DTF printing is unavailable right now. Please order on WhatsApp.';
      return '';
    }

    var resizeQueued = false;
    function nudgeStickyGallery() {
      // print-configurator.js re-measures the sticky left column on resize
      if (resizeQueued) return;
      resizeQueued = true;
      requestAnimationFrame(function () {
        resizeQueued = false;
        window.dispatchEvent(new Event('resize'));
      });
    }

    function refresh() {
      totals = computeTotals();
      renderSheet();

      var has = items.length > 0;
      actionsEl.classList.toggle('has-files', has);
      noteWrap.hidden = !has;
      labelSheet.textContent = has ? 'Add a ready sheet' : 'Upload a ready sheet';
      labelDesign.textContent = has ? 'Add a design' : 'Upload any design';

      var m = fmtMetres(totals.totalIn);
      // Like the old site: no ₹0.00 totals until there is something to print
      summaryEmpty.hidden = totals.totalIn > 0;
      summaryCalc.hidden = !(totals.totalIn > 0);
      lenLabel.textContent = m + ' m × ' + formatRate(pricePerMetre);
      lenVal.textContent = formatMoney(totals.total);
      piecesEl.textContent = plural(totals.pieces, 'piece', 'pieces') + ' · price per metre';
      billedEl.textContent = totals.billedCm ? 'Billed as ' + totals.billedCm + ' cm' : '';
      grandEl.textContent = formatMoney(totals.total);

      var reason = blockingReason();
      submitBtn.classList.toggle('is-disabled', !!reason);
      submitBtn.setAttribute('aria-disabled', reason ? 'true' : 'false');
      nudgeStickyGallery();
      saveDraftSoon();
    }

    window.addEventListener('resize', function () {
      if (layout.rects.length) {
        var cssW = canvasWrap.clientWidth || 400;
        drawSheet(canvas, layout, cssW, false);
      }
    });

    // ---------- Inputs, drag & drop ----------

    root.querySelectorAll('[data-dsb-input]').forEach(function (input) {
      input.addEventListener('change', function () {
        if (input.files && input.files.length) addFiles(input.files, input.getAttribute('data-dsb-input'));
        input.value = '';
      });
    });

    var dragDepth = 0;
    dropzone.addEventListener('dragenter', function (e) { e.preventDefault(); dragDepth++; dropzone.classList.add('is-dragover'); });
    dropzone.addEventListener('dragover', function (e) { e.preventDefault(); });
    dropzone.addEventListener('dragleave', function () {
      dragDepth = Math.max(0, dragDepth - 1);
      if (!dragDepth) dropzone.classList.remove('is-dragover');
    });
    dropzone.addEventListener('drop', function (e) {
      e.preventDefault();
      dragDepth = 0;
      dropzone.classList.remove('is-dragover');
      if (e.dataTransfer && e.dataTransfer.files.length) addFiles(e.dataTransfer.files, 'design');
    });

    // ---------- Cart ----------

    async function layoutBlob() {
      var c = document.createElement('canvas');
      var cssW = SHEET_W * 40; // 40 px per inch is plenty for a placement guide
      drawSheet(c, layout, cssW, true);
      return canvasToBlob(c, 'image/png');
    }

    async function submitOrder() {
      errorEl.textContent = '';
      statusEl.textContent = '';
      var reason = blockingReason();
      if (reason) {
        errorEl.textContent = reason;
        return;
      }

      var spinner = submitBtn.querySelector('.ppc__btn-spinner');
      submitBtn.disabled = true;
      if (spinner) spinner.hidden = false;
      statusEl.textContent = 'Uploading your files…';

      try {
        var fd = new FormData();
        fd.append('id', unit.id);
        fd.append('quantity', totals.billedCm);
        fd.append('properties[Print]', 'DTF, 22.5 in wide');
        fd.append('properties[Total length]', fmtIn(totals.totalIn) + ' in (' + fmtMetres(totals.totalIn) + ' m)');
        fd.append('properties[Billed length]', totals.billedCm + ' cm');

        if (layout.rects.length) {
          fd.append('properties[Designs sheet]', SHEET_W + ' × ' + fmtIn(layout.length) + ' in');
          totals.designs.forEach(function (d, i) {
            var n = i + 1;
            fd.append('properties[Design ' + n + ']', d.file, d.file.name);
            fd.append('properties[Design ' + n + ' size]',
              fmtIn(d.widthIn) + ' × ' + fmtIn(d.widthIn * d.aspect) + ' in × ' + plural(d.pieces, 'pc', 'pcs'));
          });
          var blob = await layoutBlob();
          if (blob) fd.append('properties[Sheet layout]', blob, 'sheet-layout.png');
        }

        totals.sheets.forEach(function (s, i) {
          var n = i + 1;
          fd.append('properties[Ready sheet ' + n + ']', s.file, s.file.name);
          fd.append('properties[Ready sheet ' + n + ' size]', SHEET_W + ' × ' + fmtIn(s.heightIn) + ' in × ' + plural(s.pieces, 'pc', 'pcs'));
        });

        if (notes && notes.value.trim()) fd.append('properties[Printer note]', notes.value.trim());

        var res = await fetch(data.cartAddUrl, { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('Failed to add to cart');

        items.slice().forEach(removeItem);
        if (notes) notes.value = '';
        clearDraft();
        statusEl.textContent = 'Added to cart!';
        if (window.dTGeneral && typeof window.dTGeneral.showMiniCart === 'function') {
          try { window.dTGeneral.showMiniCart(); } catch (e) {}
        }
      } catch (err) {
        statusEl.textContent = '';
        errorEl.textContent = 'Something went wrong adding this to your cart. Please try again.';
      } finally {
        submitBtn.disabled = false;
        if (spinner) spinner.hidden = true;
      }
    }

    submitBtn.addEventListener('click', submitOrder);

    var waBtn = q('[data-dsb-whatsapp]');
    if (waBtn) {
      waBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var cleanPhone = (waBtn.getAttribute('data-phone') || '9211537151').replace(/\D/g, '');
        if (cleanPhone.length === 10) cleanPhone = '91' + cleanPhone;
        if (!cleanPhone) cleanPhone = '919211537151';

        var msg = [
          waBtn.getAttribute('data-greeting') || 'Hello My Print Warrior, I would like to order:',
          '',
          '📦 *Product:* ' + (waBtn.getAttribute('data-product-title') || document.title)
        ];
        if (totals && totals.totalIn > 0) {
          msg.push('', '📋 *Files:*');
          totals.sheets.forEach(function (s) {
            msg.push(' • Ready sheet ' + s.name + ': ' + SHEET_W + ' × ' + fmtIn(s.heightIn) + ' in × ' + plural(s.pieces, 'pc', 'pcs'));
          });
          totals.designs.forEach(function (d) {
            msg.push(' • Design ' + d.name + ': ' + fmtIn(d.widthIn) + ' × ' + fmtIn(d.widthIn * d.aspect) + ' in × ' + plural(d.pieces, 'pc', 'pcs'));
          });
          msg.push('📏 *Total length:* ' + fmtIn(totals.totalIn) + ' in (' + fmtMetres(totals.totalIn) + ' m)');
          msg.push('💰 *Estimated Total:* ' + formatMoney(totals.total));
          msg.push('', 'I will share the files here.');
        }
        var noteVal = notes && notes.value ? notes.value.trim() : '';
        if (noteVal) msg.push('📝 *Notes:* ' + noteVal);
        msg.push('', '🔗 *Link:* ' + window.location.href);

        window.open('https://api.whatsapp.com/send?phone=' + cleanPhone + '&text=' + encodeURIComponent(msg.join('\n')), '_blank');
      });
    }

    // ---------- Lightbox (our prints + whole sheet) ----------

    var modal = q('[data-dsb-modal]');
    document.body.appendChild(modal); // escape any transformed/overflow ancestors
    var mBody = modal.querySelector('[data-dsb-modal-body]');
    var mCount = modal.querySelector('[data-dsb-modal-count]');
    var mCaption = modal.querySelector('[data-dsb-modal-caption]');
    var mPrev = modal.querySelector('[data-dsb-modal-prev]');
    var mNext = modal.querySelector('[data-dsb-modal-next]');
    var mIndex = 0;
    var prevOverflow = '';

    function openModal() {
      if (modal.hidden) {
        prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      }
      modal.hidden = false;
      modal.querySelector('[data-dsb-modal-close]').focus();
    }

    function closeModal() {
      var v = mBody.querySelector('video');
      if (v) v.pause();
      mBody.innerHTML = '';
      modal.hidden = true;
      document.body.style.overflow = prevOverflow;
    }

    function showGallery(i) {
      mIndex = Math.max(0, Math.min(gallery.length - 1, i));
      var g = gallery[mIndex];
      var old = mBody.querySelector('video');
      if (old) old.pause();
      mBody.className = 'dsb__modal-body';
      if (g.type === 'video') {
        mBody.innerHTML = '<video src="' + g.src + '" poster="' + g.poster + '" controls autoplay playsinline></video>';
        var v = mBody.querySelector('video');
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      } else {
        mBody.innerHTML = '<img class="is-zoomable" src="' + g.src + '" alt="' + escapeHtml(g.caption) + '">';
        mBody.querySelector('img').addEventListener('click', function () {
          mBody.classList.toggle('is-zoomed');
        });
      }
      mCount.textContent = (mIndex + 1) + ' / ' + gallery.length;
      mCaption.innerHTML = escapeHtml(g.caption) + (g.type === 'image' ? '<small>Click the photo to zoom in</small>' : '');
      mPrev.hidden = mIndex === 0;
      mNext.hidden = mIndex === gallery.length - 1;
      openModal();
    }

    modal.querySelector('[data-dsb-modal-close]').addEventListener('click', closeModal);
    mPrev.addEventListener('click', function () { showGallery(mIndex - 1); });
    mNext.addEventListener('click', function () { showGallery(mIndex + 1); });
    modal.addEventListener('click', function (e) {
      if (e.target === modal || e.target === mBody) closeModal();
    });
    document.addEventListener('keydown', function (e) {
      if (modal.hidden) return;
      if (e.key === 'Escape') closeModal();
      else if (mIndex >= 0 && e.key === 'ArrowRight' && mIndex < gallery.length - 1) showGallery(mIndex + 1);
      else if (mIndex >= 0 && e.key === 'ArrowLeft' && mIndex > 0) showGallery(mIndex - 1);
    });
    var touchX = null;
    modal.addEventListener('touchstart', function (e) { touchX = e.touches[0].clientX; }, { passive: true });
    modal.addEventListener('touchend', function (e) {
      if (touchX === null || mIndex < 0 || mBody.classList.contains('is-zoomed')) return;
      var dx = e.changedTouches[0].clientX - touchX;
      touchX = null;
      if (dx < -50 && mIndex < gallery.length - 1) showGallery(mIndex + 1);
      else if (dx > 50 && mIndex > 0) showGallery(mIndex - 1);
    });

    // ---------- Dialog (whole designs sheet, file preview) ----------

    var dialog = q('[data-dsb-dialog]');
    document.body.appendChild(dialog);
    var dTitle = dialog.querySelector('[data-dsb-dialog-title]');
    var dTools = dialog.querySelector('[data-dsb-dialog-tools]');
    var dBody = dialog.querySelector('[data-dsb-dialog-body]');
    var dialogRender = null;

    function openDialog(render) {
      if (dialog.hidden) {
        prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      }
      dialog.hidden = false;
      dialogRender = render;
      render();
      dialog.querySelector('[data-dsb-dialog-close]').focus();
    }

    function closeDialog() {
      dialog.hidden = true;
      dialogRender = null;
      dBody.innerHTML = '';
      document.body.style.overflow = prevOverflow;
    }

    // Artwork on a checkerboard with width/height rulers, like the sheet preview
    function figure(wLabel, hLabel, cssW, cssH, draw) {
      dBody.innerHTML = '<div class="dsb__figure" style="width:' + (cssW + 28) + 'px">' +
        '<div class="dsb__fig-x"><span>' + wLabel + '</span></div>' +
        '<div class="dsb__fig-row"><div class="dsb__fig-y"><span>' + hLabel + '</span></div>' +
        '<div class="dsb__fig-box"><canvas></canvas></div></div></div>';
      draw(dBody.querySelector('canvas'), cssW, cssH);
    }

    function showWholeSheet(mode) {
      mode = mode === 'zoom' ? 'zoom' : 'whole';
      openDialog(function () {
        dTitle.innerHTML = '<span class="dsb__dialog-name">Designs sheet</span>';
        dTools.innerHTML = '<button type="button" data-mode="whole" class="' + (mode === 'whole' ? 'is-active' : '') + '">Whole sheet</button>' +
          '<button type="button" data-mode="zoom" class="' + (mode === 'zoom' ? 'is-active' : '') + '">Zoom in</button>';
        dTools.querySelectorAll('button').forEach(function (b) {
          b.addEventListener('click', function () { showWholeSheet(b.getAttribute('data-mode')); });
        });
        var avail = Math.max(200, dBody.clientWidth - 32 - 28);
        var cssW = mode === 'zoom' ? Math.max(avail * 1.8, 640) : Math.min(avail, 360);
        figure(SHEET_W + ' in', fmtIn(layout.length) + ' in', cssW, 0, function (c, w) {
          drawSheet(c, layout, w, false);
        });
      });
    }

    function showFilePreview(item) {
      openDialog(function () {
        var list = items.filter(function (it) { return it.status === 'ready'; });
        var i = list.indexOf(item);
        if (i < 0) { closeDialog(); return; }
        var wIn = item.kind === 'sheet' ? SHEET_W : item.widthIn;
        var hIn = item.kind === 'sheet' ? item.heightIn : item.widthIn * item.aspect;
        dTitle.innerHTML = '<button type="button" class="dsb__dialog-arrow" data-dir="-1" aria-label="Previous file"' + (i === 0 ? ' disabled' : '') + '>' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg></button>' +
          '<span class="dsb__dialog-name">' + escapeHtml(item.name) + '</span><small>' + (i + 1) + ' of ' + list.length + '</small>' +
          '<button type="button" class="dsb__dialog-arrow" data-dir="1" aria-label="Next file"' + (i === list.length - 1 ? ' disabled' : '') + '>' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></button>';
        dTitle.querySelectorAll('[data-dir]').forEach(function (b) {
          b.addEventListener('click', function () {
            var next = list[i + parseInt(b.getAttribute('data-dir'), 10)];
            if (next) showFilePreview(next);
          });
        });
        dTools.innerHTML = '';
        var availW = Math.max(160, dBody.clientWidth - 32 - 28);
        var availH = Math.max(160, dBody.clientHeight - 32 - 22);
        var k = Math.min(availW / wIn, availH / hIn);
        figure(fmtIn(wIn) + ' in', fmtIn(hIn) + ' in', wIn * k, hIn * k, function (c, w, h) {
          var dpr = window.devicePixelRatio || 1;
          c.width = Math.round(w * dpr);
          c.height = Math.round(h * dpr);
          c.style.width = w + 'px';
          c.style.height = h + 'px';
          c.getContext('2d').drawImage(item.preview, 0, 0, c.width, c.height);
        });
      });
    }

    dialog.querySelector('[data-dsb-dialog-close]').addEventListener('click', closeDialog);
    dialog.addEventListener('click', function (e) { if (e.target === dialog) closeDialog(); });
    document.addEventListener('keydown', function (e) {
      if (!dialog.hidden && e.key === 'Escape') closeDialog();
    });
    window.addEventListener('resize', function () { if (dialogRender) dialogRender(); });
    wholeBtn.addEventListener('click', function () { showWholeSheet('whole'); });

    // ---------- Draft: files and settings survive a reload (kept in this browser only) ----------

    var draftKey = 'dtf-sheet-' + (data.productId || sectionId);
    var draftTimer = null;
    var restoring = true;

    function draftDb() {
      return new Promise(function (resolve, reject) {
        var req = indexedDB.open('mpw-dtf-drafts', 1);
        req.onupgradeneeded = function () { req.result.createObjectStore('drafts'); };
        req.onsuccess = function () { resolve(req.result); };
        req.onerror = function () { reject(req.error); };
      });
    }

    function draftStore(mode, fn) {
      return draftDb().then(function (db) {
        return new Promise(function (resolve, reject) {
          var tx = db.transaction('drafts', mode);
          var r = fn(tx.objectStore('drafts'));
          tx.oncomplete = function () { resolve(r && r.result); };
          tx.onerror = function () { reject(tx.error); };
        });
      });
    }

    function saveDraftSoon() {
      if (restoring) return;
      clearTimeout(draftTimer);
      draftTimer = setTimeout(function () {
        var rec = {
          notes: notes ? notes.value : '',
          items: items.filter(function (it) { return it.status !== 'error'; }).map(function (it) {
            return { kind: it.kind, name: it.name, type: it.file.type, blob: it.file, pieces: it.pieces, widthIn: it.widthIn || null };
          })
        };
        draftStore('readwrite', function (st) { return st.put(rec, draftKey); }).catch(function () {});
      }, 400);
    }

    function clearDraft() {
      clearTimeout(draftTimer);
      draftStore('readwrite', function (st) { return st.delete(draftKey); }).catch(function () {});
    }

    if (notes) notes.addEventListener('input', saveDraftSoon);

    function restoreDraft() {
      if (!window.indexedDB) { restoring = false; return; }
      draftStore('readonly', function (st) { return st.get(draftKey); }).then(function (rec) {
        if (rec && rec.items && rec.items.length) {
          rec.items.forEach(function (d) {
            var file = d.blob instanceof File ? d.blob : new File([d.blob], d.name, { type: d.type });
            addFiles([file], d.kind, { pieces: d.pieces, widthIn: d.widthIn });
          });
          if (notes && rec.notes) notes.value = rec.notes;
        }
      }).catch(function () {}).then(function () {
        restoring = false;
        refresh();
      });
    }

    var printsRow = q('[data-dsb-prints]');
    var visible = Math.min(data.galleryVisible || 6, gallery.length);
    var thumbsHtml = '';
    for (var i = 0; i < visible; i++) {
      var g = gallery[i];
      thumbsHtml += '<button type="button" class="dsb__print-thumb" data-dsb-open="' + i + '" aria-label="' +
        (g.type === 'video' ? 'Video' : 'Photo') + ' ' + (i + 1) + ' of ' + gallery.length + ': ' + escapeHtml(g.caption) + '">' +
        '<img src="' + g.thumb + '" alt="" width="96" height="96" loading="lazy">' +
        (g.type === 'video'
          ? '<span class="dsb__print-play"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg></span>' +
            (g.duration ? '<span class="dsb__print-dur">' + g.duration + '</span>' : '')
          : '') +
        '</button>';
    }
    if (gallery.length > visible) {
      thumbsHtml += '<button type="button" class="dsb__print-more" data-dsb-open="' + visible + '" aria-label="See ' +
        (gallery.length - visible) + ' more prints">+' + (gallery.length - visible) + '</button>';
    }
    printsRow.innerHTML = thumbsHtml;
    restoreDraft();
    printsRow.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-dsb-open]');
      if (btn) showGallery(parseInt(btn.getAttribute('data-dsb-open'), 10));
    });

    refresh();
  }

  function init() {
    document.querySelectorAll('.ppc--sheet').forEach(initSheetBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
